import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, inject, signal, viewChild } from '@angular/core';
import {
  OrderDisplayType,
  OrdersActionLevel,
  OrdersActionPayload,
  OrdersActionType,
} from '@app/orders/models/orders.model';
import { OrdersService } from '@app/orders/services/orders.service';
import { Constants } from '@core/app-constants';
import { DestroyableComponent } from '@core/components/destroyable/destroyable.component';
import { AlertService } from '@core/services/alert.service';
import {
  DownloadTypeEnumRequestDto,
  GetOrderItemDetailsResponseDto,
  OrderArticleProductResponseDto,
  OrderArticleSeatResponseDto,
  BlockedTicketTypeDto,
  OrderTicketTypesResponseDto,
  PrintActionEnumRequestDto,
  PrintTicketActionEnumRequestDto,
  PrintTicketTypeEnumResponseDto,
} from '@core/services/apiclient';
import { PrintService } from '@core/services/printing/print.service';
import { SalesUtilsService } from '@sales/services/sales-utils.service';
import { Store, select } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import * as fromOrders from '@root-store/orders-store/index';
import {
  PrintTicketDetailsDialogComponent,
  PrintTicketStatusDialogComponent,
  PrintTicketTypeDialogComponent,
} from '@shared/components';
import { RumService } from '@core/services/rum.service';
import { SharedModule } from '@shared/shared.module';
import { Utils } from '@shared/utils/utils';
import { Observable, Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'app-orders-print-dialog',
  templateUrl: './orders-print-dialog.component.html',
  styleUrls: ['./orders-print-dialog.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [SharedModule],
})
export class OrdersPrintDialogComponent extends DestroyableComponent implements OnInit {
  private readonly store = inject<Store<fromOrders.State>>(Store);
  private readonly printService = inject(PrintService);
  private readonly alertService = inject(AlertService);
  private readonly utils = inject(Utils);
  private readonly ref = inject(ChangeDetectorRef);
  private readonly translate = inject(TranslateService);
  private readonly ordersService = inject(OrdersService);
  private readonly salesUtils = inject(SalesUtilsService);
  private readonly rumService = inject(RumService);

  public currentFolder = this.store.selectSignal(fromOrders.selectors.getSelectedOrderItem);
  public ticketTypes = signal<OrderTicketTypesResponseDto[]>(null);
  // The rest of the envelope: mediums that can render nothing, and the tickets
  // that belong to a product line. Both are needed to explain a greyed tile.
  public blockedTicketTypes = signal<BlockedTicketTypeDto[]>([]);
  public productSeatCount = signal<number>(0);
  // The capability call did not answer. The dialog is already open by then, so
  // the sentence lives in it rather than in a toast.
  public availabilityUnknown = signal<boolean>(false);
  public printCustomerEmail = signal<string>('');

  printDialog = viewChild<PrintTicketTypeDialogComponent>(PrintTicketTypeDialogComponent);
  printDetailsDialog = viewChild<PrintTicketDetailsDialogComponent>(PrintTicketDetailsDialogComponent);
  printStatusDialog = viewChild<PrintTicketStatusDialogComponent>(PrintTicketStatusDialogComponent);

  private printDialogClosed$ = new Subject<void>();
  public isCancelStepSuccess$: Observable<boolean>;

  //payload
  public printDisplayType: OrderDisplayType;
  public printAction: PrintActionEnumRequestDto | PrintTicketActionEnumRequestDto;
  public orderActionLevel: OrdersActionLevel;

  public selectedFolders: GetOrderItemDetailsResponseDto[];
  public selectedSeats: OrderArticleSeatResponseDto[] | OrderArticleProductResponseDto[];

  public printOrderId?: number;
  public printOrderItemIds?: number[];
  public printSeatIds?: number[];
  public printSubscriptionOrderIds?: number[];

  public hasEvents: boolean = false;
  public hasSubscriptions: boolean = false;

  public printType: PrintTicketTypeEnumResponseDto;
  public printMode: 'direct' | 'detailed' | 'none' = 'none';
  public ticketTypeEnum = PrintTicketTypeEnumResponseDto;

  ngOnInit(): void {
    this.store
      .pipe(select(fromOrders.selectors.selectOrderActionsPayload), takeUntil(this.destroy$))
      .subscribe({
        next: (payload) => {
          if (payload && this.isPrintAction(payload.orderActionType)) {
            this.printAction = this.getPrintAction(payload.orderActionType);
            this.orderActionLevel = payload?.level;
            this.printDisplayType = payload.displayType;
            this.initPrintingData(payload);
            this.printDialog()?.open();
          } else {
            if (this.printDialog()) {
              this.printDialog()?.close();
              this.printDialogClosed$.next();
            }
          }
          this.ref.markForCheck();
        },
      });
  }

  private initPrintingData(payload: OrdersActionPayload) {
    let hasOnlyNonPrintableProducts = false;
    const isSubscription = (folder: GetOrderItemDetailsResponseDto) => folder.packageId > 0;
    switch (payload.displayType) {
      case OrderDisplayType.OrderSelection: {
        this.selectedFolders = payload.items;
        this.printOrderId = this.selectedFolders[0]?.orderId;

        this.hasEvents = this.selectedFolders.some((folder) => !isSubscription(folder));
        this.hasSubscriptions = this.selectedFolders.some(isSubscription);
        break;
      }

      case OrderDisplayType.SingleFolderSelection:
      case OrderDisplayType.MultipleFolderSelection: {
        this.selectedFolders = payload.items;
        this.printOrderId = null;
        hasOnlyNonPrintableProducts = this.selectedFolders.every(
          (folder) => folder.isProductPrintable === true,
        );
        const printableFolders = this.selectedFolders.filter((folder) => folder.isProductPrintable !== true);
        this.printOrderItemIds = printableFolders.length > 0 ? printableFolders.map((i) => i.id) : undefined;
        this.hasEvents = printableFolders.some((folder) => !isSubscription(folder));
        this.hasSubscriptions = printableFolders.some(isSubscription);
        break;
      }
      case OrderDisplayType.MultipleSubscriptionSelection: {
        this.selectedFolders = payload.items;
        this.printOrderId = null;
        this.printOrderItemIds = payload?.items?.map((orderItem) => orderItem.id);
        this.printSubscriptionOrderIds = payload?.orderSubscriptions?.map(
          (orderSubscription) => orderSubscription.id,
        );
        this.hasEvents = !!(this.printOrderItemIds?.length || this.printSeatIds?.length);
        this.hasSubscriptions = !!this.printSubscriptionOrderIds?.length;
        break;
      }

      case OrderDisplayType.SingleSeatSelection:
      case OrderDisplayType.MultipleSeatSelection: {
        this.selectedSeats = payload.items;
        this.printOrderId = this.currentFolder().orderId;
        hasOnlyNonPrintableProducts = this.selectedSeats.every(
          (seat) => (seat as any).isProductPrintable === true,
        );
        const printableSeats = this.selectedSeats.filter((seat) => (seat as any).isProductPrintable !== true);
        this.printSeatIds = printableSeats.length > 0 ? printableSeats.map((s) => s.id) : undefined;
        this.hasEvents = true;
        this.hasSubscriptions = false;
        break;
      }
    }

    this.printCustomerEmail.set(this.getCustomerEmail());

    if (hasOnlyNonPrintableProducts) {
      // A legitimate empty, decided here without asking: the flag stays down so
      // the subtitle keeps naming the product lines.
      this.availabilityUnknown.set(false);
      this.ticketTypes.set([]);
      return;
    }

    this.loadAvailableTicketTypes();
  }

  private loadAvailableTicketTypes() {
    // Lowered on the way OUT, so a retry after a failure does not keep claiming
    // "could not check" for the whole duration of a call that may succeed.
    this.availabilityUnknown.set(false);

    this.ordersService
      .getOrderAvailableTicketTypes({
        action: this.printAction as PrintTicketActionEnumRequestDto,
        orderId: this.printOrderId,
        orderItemIds: this.printOrderItemIds,
        seatIds: this.printSeatIds,
        subscriptionOrderIds: this.printSubscriptionOrderIds,
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.availabilityUnknown.set(false);
          this.ticketTypes.set(response.ticketTypes ?? []);
          this.blockedTicketTypes.set(response.blockedTicketTypes ?? []);
          this.productSeatCount.set(response.productSeatCount ?? 0);
        },
        // Clearing the lists is part of raising the flag, not a detail: the
        // dialog only renders its subtitle on an empty list, and a list left
        // over from a previous print would keep its tiles live on an answer
        // nobody received. This component survives a print cycle, and neither
        // reset path below clears them.
        // Reported, for the same reason as its twin in `sales-summary`: a handler
        // that neither rethrows nor traces removes the failure from the RUM, where
        // `window.onerror` used to collect it. A 5xx still reaches the global
        // `ErrorInterceptor`; a 4xx would become invisible, and this screen names
        // a cause that nobody could then diagnose.
        error: (err: unknown) => {
          this.rumService.datadogTraceError(
            'orders_print_availability_failed',
            err instanceof Error ? err.message : String(err),
            {},
          );

          this.availabilityUnknown.set(true);
          this.ticketTypes.set([]);
          this.blockedTicketTypes.set([]);
          this.productSeatCount.set(0);
        },
      });
  }

  private getCustomerEmail() {
    return this.printDisplayType == OrderDisplayType.OrderSelection
      ? this.selectedFolders.filter((f) => f.customer?.email)?.[0]?.customer.email
      : this.defaultFolder()?.customer?.email;
  }

  private getPrintAction(actionType: OrdersActionType) {
    switch (actionType) {
      case OrdersActionType.Print:
        return PrintActionEnumRequestDto.Print;
      case OrdersActionType.Duplication:
        return PrintActionEnumRequestDto.Duplicate;
    }
  }

  private isPrintAction = (actionType: OrdersActionType) =>
    actionType === OrdersActionType.Print || actionType === OrdersActionType.Duplication;

  private foldersWithSameOrderId() {
    return (
      this.selectedFolders?.length &&
      this.selectedFolders.every((folder, index, self) => folder?.orderId === self?.[0]?.orderId)
    );
  }
  private readonly defaultFolder = (): GetOrderItemDetailsResponseDto =>
    this.printDisplayType == OrderDisplayType.SingleSeatSelection ||
    this.printDisplayType == OrderDisplayType.MultipleSeatSelection
      ? this.currentFolder()
      : this.selectedFolders?.[0];

  private getTicketCountForType(printType?: PrintTicketTypeEnumResponseDto): number {
    // Sans type (chemin « tous types » éventuel), on somme tout : la garde reste conservatrice.
    return (this.ticketTypes() ?? [])
      .filter((type) => printType == null || type.ticketType === printType)
      .reduce((total, type) => total + (type.seatCount ?? 0), 0);
  }

  private exceedsTicketLimit(printType?: PrintTicketTypeEnumResponseDto): boolean {
    const totalTickets = this.getTicketCountForType(printType);
    if (totalTickets <= Constants.MaxBasketSeatsCount) {
      return false;
    }
    this.salesUtils.confirmTicketLimitExceeded(totalTickets);
    return true;
  }

  public printOrder(
    validationType: 'direct' | 'detailed' | 'none',
    printType?: PrintTicketTypeEnumResponseDto,
    downloadType?: DownloadTypeEnumRequestDto,
  ) {
    // Garde anti-OOM : refuser une impression > MaxBasketSeatsCount billets RÉELS avant l'appel API.
    // Le back renvoie déjà par type le nombre réel de billets (scope event-session = COUNT(Article),
    // soit place × séance pour un abonnement) — pas besoin de recompter côté front.
    if (validationType !== 'none' && this.exceedsTicketLimit(printType)) {
      return;
    }
    this.printType = printType;
    this.printMode = validationType;
    this.ref.detectChanges();
    switch (validationType) {
      case 'direct': {
        switch (printType) {
          case PrintTicketTypeEnumResponseDto.Pdf:
            this.printAllAsPdf(downloadType);
            break;
          case PrintTicketTypeEnumResponseDto.Boca:
          case PrintTicketTypeEnumResponseDto.Card:
            this.openPrintStatusDialog();
            break;
        }
        break;
      }
      case 'detailed': {
        this.openPrintDetailsDialog();
        break;
      }
      case 'none': {
        this.resetPrinting();
        break;
      }
    }
  }

  public openPrintDetailsDialog(): void {
    this.printDetailsDialog()?.open();
  }

  public openPrintStatusDialog(): void {
    this.printService.startPrinting({
      printAction: this.printAction as PrintTicketActionEnumRequestDto,
      printType: this.printType,
      orderId: this.printOrderId,
      orderItemIds: this.printOrderItemIds,
      subscriptionOrderIds: this.printSubscriptionOrderIds,
      seatIds: this.printSeatIds,
    });

    this.printStatusDialog()?.open();
  }

  private printAllAsPdf(downloadType: DownloadTypeEnumRequestDto) {
    this.printService
      .printAsPdf({
        action: this.printAction as PrintActionEnumRequestDto,
        downloadType: downloadType,
        orderId: this.printOrderId,
        orderItemIds: this.printOrderItemIds,
        seatIds: this.printSeatIds,
        subscriptionOrderIds: this.printSubscriptionOrderIds,
      })
      .subscribe({
        next: (response) => {
          if (response.isJob) {
            this.alertService.showCustom('printing.yourPDFGenerationRequest', 'success');
            this.store.dispatch(fromOrders.actions.resetOrderAction());
            this.store.dispatch(fromOrders.actions.printRequestCompleted());
            this.resetPrinting();
          } else {
            this.utils.saveToFileSystem(
              response.data,
              response?.fileName,
              response?.headers?.['content-type'],
            );
            this.alertService.showCustom(this.getPrintSuccessMessage(), 'success');
            this.onPrintSuccess();
          }
          this.ref.markForCheck();
        },
      });
  }

  public onPrintSuccess() {
    this.store.dispatch(
      fromOrders.actions.processOrderEditSuccess({
        orderItemId: this.currentFolder()?.id,
        orderId: this.currentFolder()?.orderId,
        level: this.orderActionLevel,
        printOrderId: this.printOrderId,
        printOrderItemIds: this.printOrderItemIds,
        printSeatIds: this.printSeatIds,
        printSubscriptionOrderIds: this.printSubscriptionOrderIds,
        orderDisplayType: this.printDisplayType,
      }),
    );
    this.resetPrinting();
  }

  public resetPrinting() {
    this.printType = null;
    this.printMode = 'none';
    this.printOrderId = null;
    this.printOrderItemIds = null;
    this.printSeatIds = null;
    this.printSubscriptionOrderIds = null;
    this.printDetailsDialog()?.close();
    this.printDialog()?.close();
  }

  private getPrintSuccessMessage() {
    switch (this.printDisplayType) {
      case OrderDisplayType.OrderSelection:
        return this.translate.instant('printing.info.orderNumberPrintedSuccessfully', {
          printOrderId: this.printOrderId,
        });
      case OrderDisplayType.SingleFolderSelection:
      case OrderDisplayType.MultipleFolderSelection:
        return this.printOrderItemIds.length == 1
          ? this.translate.instant('printing.info.folderNumberPrintedSuccessfully', {
              printOrderItemIds: this.printOrderItemIds[0],
            })
          : 'printing.info.selectionPrintedSuccessfully';
      case OrderDisplayType.SingleSeatSelection:
      case OrderDisplayType.MultipleSeatSelection:
        return this.selectedSeats.length == 1
          ? this.translate.instant('printing.info.articleNumberPrintedSuccessfully', {
              seat: this.selectedSeats.map((seat) => seat?.seat),
            })
          : 'printing.info.selectionPrintedSuccessfully';
      case OrderDisplayType.MultipleSubscriptionSelection:
        return 'printing.info.selectionPrintedSuccessfully';
    }
  }
}
